import { getUser, getUserAddress, getUserPhoneNumber, updateUserAddress, updateUserPhoneNumber } from 'core/api';
import { useAuthorization } from 'core/context';
import { useToast } from 'core/toast';
import { IUser, IUserAddress, IUserPhone, normalizePhone } from 'core/utilities';
import React, { useEffect, useState } from 'react';
import { getAddressField, getPlaceDetails } from 'view/components/common';

const useProfileHook = () => {
    const authState = useAuthorization();
    const { setToastMessage } = useToast();

    const [ phoneEditMode, setPhoneEditMode ] = useState(false);
    const [ addressEditMode, setAddressEditMode ] = useState(false);
    const [ currentUser, setCurrentUser ] = useState<IUser | undefined>();
    const [ addressId, setAddressId ] = useState('');
    const [ phoneId, setPhoneId ] = useState('');

    const [ phoneNumber, setPhoneNumber ] = useState('');
    const [ fullAddress, setFullAddress ] = useState('');
    const [ addressToBeSubmitted, setAddressToBeSubmitted ] = useState<IUserAddress | null>(null);

    const [ suiteNumber, setSuiteNumber ] = useState({
        type: '',
        number: ''
    });

    const enablePhoneEditMode = () => {
        setPhoneEditMode(true);
        setAddressEditMode(false);
    };

    const enableAddressEditMode = () => {
        setPhoneEditMode(false);
        setAddressEditMode(true);
    };

    const disableEditMode = () => {
        setPhoneEditMode(false);
        setAddressEditMode(false);
    };

    const getCurrentUser = async () => {
        const { user } = await getUser(authState);

        if (!user) return setToastMessage('Authentication failed. Please log in again');

        setCurrentUser(user);
    };

    const getCurrentUserAddress = async () => {
        const { success, userAddress } = await getUserAddress(authState);

        if (!success) return setToastMessage('Failed to load user address');

        const { id, address, city, region, country, postal, suite } = userAddress;
        setAddressId(id);

        const full_address = `${address}, ${city}, ${region}, ${country}`;
        setFullAddress(full_address);

        setAddressToBeSubmitted({
            address,
            city,
            region,
            country,
            postal
        });

        if (!suite) return;

        const suiteType = suite.split(' ')[0];
        const suiteNumber = suite.split(' ')[1];
        setSuiteNumber({
            type: suiteType,
            number: suiteNumber
        });
    };

    const getCurrentPhoneNumber = async () => {
        const { success, phoneNumber } = await getUserPhoneNumber(authState);

        if (!success) return setToastMessage('Failed to load user phone number');

        const { id, number } = phoneNumber;
        setPhoneId(id);
        setPhoneNumber(number);
    };

    const handlePhoneChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        normalizePhone(e);
        setPhoneNumber(e.target.value);
    };

    const savePhoneNumber = async () => {
        if (!phoneNumber) return setToastMessage('Phone number is required');

        const userPhone: IUserPhone = {
            number: phoneNumber
        };

        const { success } = await updateUserPhoneNumber(phoneId, userPhone);

        if (!success) return setToastMessage('Failed to update phone number');

        setToastMessage('Phone number updated!');
        disableEditMode();
    };

    const handleAddressChange = (address: string) => {
        setFullAddress(address);
    };

    const handleAddressSelect = async (address: string, placeId: string) => {
        setFullAddress(address);

        const place = await getPlaceDetails(placeId);

        if (place) {
            const address: IUserAddress = {
                address: place.name ? place.name : '',
                city: getAddressField(place.address_components, 'locality'),
                region: getAddressField(place.address_components, 'administrative_area_level_1'),
                country: getAddressField(place.address_components, 'country', true),
                postal: getAddressField(place.address_components, 'postal_code')
            };

            setAddressToBeSubmitted(address);
        } else {
            setFullAddress('');
            setAddressToBeSubmitted(null);
        }
    };

    const handleSuiteChange = (name: string, value: string) => {
        setSuiteNumber({
            ...suiteNumber,
            [name]: value
        });
    };

    const saveAddress = async () => {
        if (!addressToBeSubmitted) return setToastMessage('Select a valid location first');

        let payload = addressToBeSubmitted;

        if (suiteNumber.type !== '' && suiteNumber.number !== '') {
            payload = {
                ...addressToBeSubmitted,
                suite: `${suiteNumber.type} ${suiteNumber.number}`
            };
        }

        const { success } = await updateUserAddress(addressId, payload);

        if (!success) return setToastMessage('Failed to update address');

        setToastMessage('Address updated!');
        disableEditMode();
    };

    useEffect(() => {
        getCurrentUser();
        getCurrentPhoneNumber();
        getCurrentUserAddress();
    }, []);

    return {
        currentUser, phoneEditMode, addressEditMode, enablePhoneEditMode, enableAddressEditMode, disableEditMode,
        phoneNumber, handlePhoneChange, savePhoneNumber, fullAddress, handleAddressChange, handleAddressSelect, saveAddress,
        suiteNumber, handleSuiteChange
    };
};

export default useProfileHook;
